/**
 * i18n message catalog (English baseline).
 *
 * Keys are dot-namespaced by surface (e.g. `common.save`, `nav.dashboard`).
 * This is the single source of translatable strings; additional locales add a
 * sibling record keyed by the same keys. English-only ships today — the goal of
 * this scaffolding is that every user-visible string flows through `t()` so a
 * future locale is a data drop-in, not a code change.
 *
 * Distinct from LabelsContext, which remaps *domain nouns* (Project→Matter) per
 * org. i18n translates the *language*; labels translate the *vocabulary*. They
 * compose: `t('nav.projects')` can itself be overridden by the org label.
 */

export type Locale = 'en'

export type Messages = Record<string, string>

export const messages: Record<Locale, Messages> = {
  en: {
    // Common actions
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.create': 'Create',
    'common.add': 'Add',
    'common.remove': 'Remove',
    'common.close': 'Close',
    'common.confirm': 'Confirm',
    'common.loading': 'Loading…',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.clear': 'Clear',
    'common.apply': 'Apply',
    'common.actions': 'Actions',
    'common.none': 'None',
    'common.required': 'Required',
    'common.optional': 'Optional',

    // Navigation
    'nav.dashboard': 'Executive Command Center',
    'nav.governance': 'Governance',
    'nav.lessons': 'Lessons Learned',
    'nav.ai': 'AI Assistant',
    'nav.templates': 'Template Editor',
    'nav.recycleBin': 'Recycle Bin',
    'nav.settings': 'Admin & Settings',
    'nav.logout': 'Logout',
    'nav.collapseSidebar': 'Collapse sidebar',
    'nav.expandSidebar': 'Expand sidebar',

    // Custom fields
    'customFields.title': 'Custom Fields',
    'customFields.empty': 'No custom fields yet.',
    'customFields.addField': 'Add Field',
    'customFields.filterBy': 'Filter by {field}',

    // Share view
    'share.timezoneNote': 'All dates shown in {tz}.',
    'share.deadlines': 'Deadlines',
    'share.passwordRequired': 'Password required',

    // Task assignment (KQA-11)
    'task.assignee.label': 'Assignee',
    'task.assignee.placeholder': 'Unassigned — select a person',
    'task.assignee.search': 'Search people by name or email',
    'task.assignee.unassigned': 'Unassigned',
    'task.assignee.noResults': 'No matching people',
    'task.assignee.clear': 'Clear assignee',
    'task.project.placeholder': 'Select a project',
    'task.project.search': 'Search projects by name',
    'task.project.noResults': 'No matching projects',

    // Status / generic
    'status.label': 'Status',

    // ── Project + task forms (Part 18b migration) ─────────────────────
    'projects.create.title': 'Create New Project',
    'projects.create.cta': 'Save Project',
    'projects.create.saving': 'Saving…',
    'projects.edit.title': 'Edit Project',
    'projects.edit.cta': 'Save Changes',
    'projects.fields.name': 'Project Name',
    'projects.fields.name.placeholder': 'e.g. Q1 Platform Migration',
    'projects.fields.description': 'Description',
    'projects.fields.description.placeholder':
      'What this project is for, who it serves, what done looks like.',
    'projects.fields.priority': 'Priority',
    'projects.fields.status': 'Status',
    'projects.fields.startDate': 'Start Date',
    'projects.fields.endDate': 'End Date',
    'projects.fields.budget': 'Budget',
    'projects.fields.manager': 'Project Manager',
    'projects.fields.portfolio': 'Portfolio',
    'projects.fields.program': 'Program',
    'projects.empty':
      'No projects yet. Create your first one to get started.',
    'projects.delete.confirm':
      'Delete this project? It can be restored from the recycle bin within 30 days.',
    'projects.saved': 'Project saved.',
    'projects.deleted': 'Project deleted.',

    'tasks.create.title': 'Create New Task',
    'tasks.create.cta': 'Save Task',
    'tasks.create.saving': 'Saving…',
    'tasks.edit.title': 'Edit Task',
    'tasks.edit.cta': 'Save Changes',
    'tasks.fields.title': 'Task Title',
    'tasks.fields.title.placeholder': 'e.g. Review submittal #14 with electrical',
    'tasks.fields.description': 'Description',
    'tasks.fields.priority': 'Priority',
    'tasks.fields.status': 'Status',
    'tasks.fields.dueDate': 'Due Date',
    'tasks.fields.assignee': 'Assignee',
    'tasks.fields.project': 'Project',
    'tasks.empty': 'No tasks yet. Add one above.',

    // Priority labels (used in dropdowns and badges)
    'priority.low': 'Low',
    'priority.medium': 'Medium',
    'priority.high': 'High',
    'priority.urgent': 'Urgent',
    'priority.critical': 'Critical',

    // Auth + signup
    'auth.signIn': 'Sign in',
    'auth.signOut': 'Sign out',
    'auth.signUp': 'Sign up',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.forgotPassword': 'Forgot password?',
    'auth.invalidCredentials': 'Email or password is incorrect.',

    // Password reset (Sprint 29)
    'auth.reset.title': 'Reset your password',
    'auth.reset.subtitle':
      "Enter your account email and we'll send you a reset link.",
    'auth.reset.emailLabel': 'Email Address',
    'auth.reset.sendLink': 'Send reset link',
    'auth.reset.sending': 'Sending…',
    'auth.reset.sentTitle': 'Check your email',
    'auth.reset.sentBody':
      'If that email is registered, a reset link is on its way. The link expires in 1 hour.',
    'auth.reset.backToLogin': 'Back to sign in',
    'auth.reset.newTitle': 'Choose a new password',
    'auth.reset.newPasswordLabel': 'New password',
    'auth.reset.confirmPasswordLabel': 'Confirm new password',
    'auth.reset.submit': 'Set new password',
    'auth.reset.submitting': 'Saving…',
    'auth.reset.mismatch': 'Passwords do not match.',
    'auth.reset.tooShort': 'Password must be at least 8 characters.',
    'auth.reset.invalidLink':
      'This reset link is invalid or has expired. Request a new one.',
    'auth.reset.missingToken':
      'This page needs the link from your reset email.',
    'auth.reset.requestNew': 'Request a new link',
    'auth.reset.success': 'Password updated. Sign in with your new password.',
    'auth.reset.genericError': 'Something went wrong. Please try again.',

    // Email verification (Sprint 30)
    'auth.verify.verifying': 'Verifying your email…',
    'auth.verify.successTitle': 'Email verified',
    'auth.verify.successBody':
      "You're all set — your account can now send team invitations.",
    'auth.verify.failTitle': 'Verification link invalid',
    'auth.verify.failBody':
      'This link is invalid or has expired. Sign in and use "Resend email" in the banner to get a new one.',
    'auth.verify.goToApp': 'Continue to Karyva',
    'auth.verify.backToLogin': 'Back to sign in',
    'auth.verify.bannerText':
      'Verify your email address — check your inbox for the link.',
    'auth.verify.resend': 'Resend email',
    'auth.verify.resending': 'Sending…',
    'auth.verify.resent': 'Verification email sent. Check your inbox.',
    'auth.verify.resendError': 'Could not send the email. Please try again.',
    'auth.verify.dismiss': 'Dismiss banner',

    // Invitation accept (Sprint 32)
    'auth.accept.title': 'Accept your invitation',
    'auth.accept.subtitle': 'Choose a password to finish setting up your account.',
    'auth.accept.passwordLabel': 'Password',
    'auth.accept.confirmLabel': 'Confirm password',
    'auth.accept.submit': 'Create account',
    'auth.accept.submitting': 'Creating…',
    'auth.accept.mismatch': 'Passwords do not match.',
    'auth.accept.tooShort': 'Password must be at least 8 characters.',
    'auth.accept.invalidLink':
      'This invitation link is invalid or has expired. Ask whoever invited you to send a new one.',
    'auth.accept.missingToken':
      'This page needs the link from your invitation email.',
    'auth.accept.backToLogin': 'Back to sign in',
    'auth.accept.success': 'Account created. Sign in to get started.',
    'auth.accept.genericError': 'Something went wrong. Please try again.',

    // Team invitations (Sprint 32)
    'invite.button': 'Invite user',
    'invite.modalTitle': 'Invite a teammate',
    'invite.emailLabel': 'Email',
    'invite.nameLabel': 'Full name',
    'invite.roleLabel': 'Role',
    'invite.roleNone': 'No role',
    'invite.accessLabel': 'Access',
    'invite.accessMember': 'Member: can create and edit work',
    'invite.accessViewer': 'Viewer: read-only, free (no seat)',
    'invite.send': 'Send invitation',
    'invite.sending': 'Sending…',
    'invite.sent': 'Invitation sent to {email}.',
    'invite.emailRequired': 'Enter an email address.',
    'invite.emailInvalid': 'Enter a valid email address.',
    'invite.unverified':
      'Verify your email address before sending invitations — check your inbox for the link.',
    'invite.error': 'Could not send the invitation. Please try again.',
    'invite.pendingTitle': 'Pending invitations',
    'invite.pendingEmpty': 'No pending invitations.',
    'invite.resend': 'Resend',
    'invite.resent': 'Invitation resent.',
    'invite.resendError': 'Could not resend the invitation.',
    'invite.revoke': 'Revoke',
    'invite.revokeConfirm': 'Revoke this invitation?',
    'invite.revoked': 'Invitation revoked.',
    'invite.revokeError': 'Could not revoke the invitation.',
    'invite.onboarding.someSent': 'Sent {count} invitation(s).',
    'invite.onboarding.someFailed': 'Could not send {count} invitation(s) — you can retry from Admin → Users.',
    'invite.onboarding.invalid': 'Skipped {count} invalid address(es): {emails}',

    'auth.sessionExpired':
      'Your session has expired. Please sign in again.',

    // Billing copy
    'billing.trial': 'Trial',
    'billing.trialEnds': 'Trial ends {date}',
    'billing.trialExpired': 'Your trial has ended. Upgrade to continue.',
    'billing.upgrade': 'Upgrade plan',
    'billing.currentPlan': 'Current plan',
    'billing.plan.starter': 'Starter',
    'billing.plan.growth': 'Growth',
    'billing.plan.business': 'Business',
    'billing.plan.enterprise': 'Enterprise',

    // ACL copy (Part 15)
    'acl.permissionsTab': 'Permissions',
    'acl.grantTitle': 'Grant access',
    'acl.revoke': 'Revoke',
    'acl.revokeConfirm': 'Revoke this grant?',
    'acl.role.viewer': 'Viewer',
    'acl.role.contributor': 'Contributor',
    'acl.role.manager': 'Manager',
    'acl.role.owner': 'Owner',
    'acl.accessDenied':
      "You don't have access to this. Ask an administrator to add you to the project.",

    // App crash fallback (Sprint 36 — ErrorBoundary). Provider-neutral copy;
    // looked up directly from the catalog because the boundary can render after
    // the i18n provider itself has crashed.
    'error.boundary.title': 'Something went wrong',
    'error.boundary.body':
      'An unexpected error occurred. Try reloading the page — if it keeps happening, contact support.',
    'error.boundary.reload': 'Reload page',

    // Generic feedback / errors
    'feedback.error': 'Something went wrong. Please try again.',
    'feedback.requiredField': 'This field is required.',
    'feedback.yes': 'Yes',
    'feedback.no': 'No',
  },
}
